import React, { useState, useEffect, useRef } from 'react';
import styles from '../../css/ChatbotContainer.module.css';
import { chatService } from '../../services/apiService';
import { useLocation } from '../../contexts/LocationContext'; 

const ChatbotContainer = ({ onClose }) => {
  const [message, setMessage] = useState('');
  const [isClosing, setIsClosing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const chatHistoryRef = useRef(null);
  const { position, error } = useLocation();
  const currentTime = new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' });
  const [chatHistory, setChatHistory] = useState([
    {
      sender: 'bot',
      message: '안녕하세요! 오늘의 날씨 정보가 필요하신가요? 질문해주시거나 버튼을 눌러보세요.',
      time: currentTime
    }
  ]);

  // Auto-scroll to bottom of chat when new messages are added
  useEffect(() => {
    if (chatHistoryRef.current) {
      chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
    }
  }, [chatHistory]);

  const handleSend = async () => {
    if (message.trim()) {
      
      // Add user message to chat
      const userMessage = {
        sender: 'user',
        message: message.trim(),
        time: currentTime
      };
      
      setChatHistory(prev => [...prev, userMessage]);
      setMessage('');
      setIsLoading(true);
      
      try {
        const apiHistory = chatHistory.map(chat => ({
          role: chat.sender === 'user' ? 'user' : 'model',
          parts: [{ text: chat.message }]
        }));
        
        const response = await chatService.sendMessage(userMessage.message, apiHistory, position);
        
        setChatHistory(prev => [...prev, {
          sender: 'bot',
          message: response.text,
          time: currentTime
        }]);
      } catch (error) {
        console.error('Error sending message:', error);
        setChatHistory(prev => [...prev, {
          sender: 'bot',
          message: '죄송합니다. 메시지를 처리하는 동안 오류가 발생했습니다.',
          time: currentTime
        }]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const quickInfoButtons = [
    { text: '☔ 강수 확률', color: '#DDA1A1' },
    { text: '🌈 주말 날씨', color: '#72C387' },
    { text: '🌤️ 내일 날씨', color: '#DDA1A1' },
    { text: '🌡️ 주간 기온', color: '#72C387' },
    { text: '🌫️ 미세먼지', color: '#77C3F5' },
    { text: '🌂 우산챙겨?', color: '#E8E4AD' },
    { text: '🧥 뭐 입지?', color: '#77C3F5' },
    { text: '⚠️ 기상 특보', color: '#E8E4AD' }
  ];

  const handleQuickInfoClick = (text) => {
    setMessage(text);
    handleSend();
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
    }, 200); 
  };

  return (
    <div className={`${styles.container} ${isClosing ? styles.closing : ''}`}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleClose}>
          &lt;
        </button>
        <h3 className={styles.title}>AI 디아이캐스터</h3>
      </div>

      <div className={styles.quickInfoSection}>
        <div className={styles.quickInfoTitle}>자주 찾는 정보</div>
        <div className={styles.quickInfoButtons}>
          {quickInfoButtons.map((button, index) => (
            <button 
              key={index} 
              className={styles.quickButton}
              style={{ backgroundColor: button.color }}
              onClick={() => handleQuickInfoClick(button.text)}
            >
              {button.text}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.chatHistory} ref={chatHistoryRef}>
        {chatHistory.map((chat, index) => (
          <div 
            key={index} 
            className={`${styles.chatBubble} ${chat.sender === 'user' ? styles.userChat : styles.botChat}`}
          >
            {chat.sender === 'bot' && (
              <div className={styles.botIcon}>
                <img src="/img/dicaster.png" alt="AI 디아이캐스터" className={styles.botAvatar} />
              </div>
            )}
            <div className={styles.messageWrapper}>
              <div className={styles.messageContainer}>
                {chat.sender === 'bot' && <div className={styles.botName}>AI 디아이캐스터</div>}
                <div className={styles.message}>
                  {chat.message}
                </div>
              </div>
              <div className={styles.messageTime}>{chat.time}</div>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className={`${styles.chatBubble} ${styles.botChat}`}>
            <div className={styles.botIcon}>
              <img src="/img/dicaster.png" alt="AI 디아이캐스터" className={styles.botAvatar} />
            </div>
            <div className={styles.messageWrapper}>
              <div className={styles.messageContainer}>
                <div className={styles.botName}>AI 디아이캐스터</div>
                <div className={styles.message}>
                  <div className={styles.typingIndicator}>
                    <span>.</span><span>.</span><span>.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.inputContainer}>
        <input
          type="text"
          className={styles.messageInput}
          placeholder="날씨에 대해 무엇이든 물어보세요..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSend()}
        />
        <button className={styles.sendButton} onClick={handleSend} disabled={isLoading}>
          <span className={styles.sendIcon}>↑</span>
        </button>
      </div>
    </div>
  );
};

export default ChatbotContainer;